import React, { useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { theme } from "../theme";
import logo from "../PhotosGallery/best-rijschool-oost-souburg-rijles-leerling.png"; // Import the logo
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";

const NavContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }) => theme.colors.text};
  gap: 1rem;
  padding: 1rem clamp(1rem, 4vw, 3rem);
  width: 100%;
  position: relative;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  
  
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const LogoImage = styled.img`
  z-index: 10;
  width: clamp(3.25rem, 9vw, 5.625rem);
  height: auto;
  margin-right: clamp(0.5rem, 2vw, 1.25rem);
  background: linear-gradient(45deg, #b8860b, #d4af37); /* Gold gradient */
  padding: 5px;
  border-radius: 10px;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.7); /* Depth effect */

  animation: logo-shine 3s infinite alternate;

  @keyframes logo-shine {
    0% {
      box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.7),
        0 0 10px rgba(212, 175, 55, 0.7);
    }
    100% {
      box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.7),
        0 0 20px rgba(212, 175, 55, 0.9), 0 0 30px rgba(212, 175, 55, 0.8);
    }
  }

  transition: transform 0.3s ease, color 0.3s ease;
  &:hover {
    transform: scale(1.08);
    color: ${theme.colors.primary};
  }
`;

const SchoolName = styled.h1`
  z-index: 10;
  font-size: clamp(1.35rem, 4vw, 3.5rem);
  margin: 1px;
  padding-left: 0;
  flex: 1;
  background: linear-gradient(45deg, #b8860b, #d4af37);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: bold;
  position: relative;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.6);

  animation: shine 3s infinite alternate;

  @keyframes shine {
    0% {
      text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.6),
        0 0 10px rgba(212, 175, 55, 0.7);
    }
    100% {
      text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.6),
        0 0 20px rgba(212, 175, 55, 0.9), 0 0 30px rgba(212, 175, 55, 0.8);
    }
  }
  transition: transform 0.3s ease, color 0.3s ease;
  &:hover {
    transform: scale(1.08);
    color: ${theme.colors.primary};
  }
`;

const NavLinks = styled.ul<{ $isOpen: boolean }>`
  list-style: none;
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 2vw, 1.5rem);
  position: relative;
  margin: 0 0 0 auto;
  padding: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    position: absolute;
    top: calc(100% - 0.25rem);
    left: 0;
    background-color: ${theme.colors.background};
    width: 100%;
    padding: 0.5rem 1rem 1rem;
    z-index: 20;
    opacity: ${(props) => (props.$isOpen ? 1 : 0)};
    visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
    transform: translateY(${(props) => (props.$isOpen ? "0" : "-0.75rem")});
    pointer-events: ${(props) => (props.$isOpen ? "auto" : "none")};
    transition: opacity 0.25s ease, transform 0.25s ease, visibility 0.25s;
  }
`;

const NavLinkItem = styled.li`
  font-size: clamp(1rem, 2vw, 1.5rem);

  & a {
    color: ${theme.colors.primary};
    text-decoration: none;
    display: block;
    padding: 0.75rem;
  }

  transition: transform 0.3s ease, color 0.3s ease;
  &:hover {
    transform: scale(1.3);
    color: ${theme.colors.primary};
  }
`;

const LanguagePosition = styled.div`
  position: absolute;
  top: 0.55rem;
  right: clamp(1rem, 4vw, 3rem);
  z-index: 11;
`;

const HamburgerMenu = styled.div`
  display: none; // Hide on large screens
  cursor: pointer;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0.5rem;
  border: 0;
  background: transparent;

  & div {
    width: 100%;
    height: 0.1875rem;
    background: darkgoldenrod;
    margin: 0.25rem 0;
    transition: all 0.3s;
  }

  @media (max-width: 768px) {
    display: block; // Show on small screens
  }
`;

const Navigation: React.FC = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const { t } = useTranslation();

  const handleLinkClick = () => {
    setIsNavOpen(false); // Close the nav when a link is clicked
  };

  return (
    <NavContainer>
      <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
        <LogoContainer>
          <LogoImage src={logo} alt="Best Rijschool Logo" />
          <SchoolName>Best Rijschool</SchoolName>
        </LogoContainer>
      </Link>
      {/* Mobile navigation stays mounted so opening and closing animates smoothly. */}
         <HamburgerMenu
        as="button"
        type="button"
        aria-label={t("nav.menu")}
        aria-expanded={isNavOpen}
        onClick={() => setIsNavOpen(!isNavOpen)}
      >
        <div />
        <div />
        <div />
      </HamburgerMenu>
      <NavLinks $isOpen={isNavOpen}>
        <NavLinkItem>
          <Link to="/" onClick={handleLinkClick}>
            {t("nav.home")}
          </Link>
        </NavLinkItem>
        <NavLinkItem>
          <Link to="/courses" onClick={handleLinkClick}>
            {t("nav.prices")}
          </Link>
        </NavLinkItem>
        <NavLinkItem>
          <Link to="/gallery" onClick={handleLinkClick}>
            {t("nav.gallery")}
          </Link>
        </NavLinkItem>
        <NavLinkItem>
          <Link to="/about" onClick={handleLinkClick}>
            {t("nav.about")}
          </Link>
        </NavLinkItem>
        <NavLinkItem>
          <Link to="/contact" onClick={handleLinkClick}>
            {t("nav.contact")}
          </Link>
        </NavLinkItem>

      </NavLinks>
      <LanguagePosition><LanguageSwitcher /></LanguagePosition>
      
    </NavContainer>
  );
};

export default Navigation;
